'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { toBigInt, useCachedValue } from '@morpho-org/dapp-core';
import { formatLongString, isDefined, type Time } from '@morpho-org/morpho-ts';
import { type OptionSelect, Select } from '@morpho-org/ui-v6/client';
import { Card, CardText, Skeleton, TableNoResults } from '@morpho-org/ui-v6/server';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { parseEther } from 'viem';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { formatTimeseriesData, isSerieEmpty, removeNullYValuesFromSeries } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { defaultTimeFrame, timeFrameOptions } from '@/config';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getParsedValue } from '@/helpers/formatters';
import { splitValueAndUnit } from '@/helpers/number';
import { toHistoryRange } from '@/helpers/timeseries';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { orpc } from '@/lib/orpc/client';
import { errorCodeOf } from '@/lib/orpc/errors';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { isUsdHistoryUnavailable } from './isUsdHistoryUnavailable';
import { VaultYourPositionChartError } from './states';
export interface VaultYourPositionChartClientProps {
  vaultAddress: Address;
  chainId: ChainId;
}
type Mode = 'assets' | 'assetsUsd';
const formatter = formatters.short.trailingZero(false).default('0.00');
const VaultYourPositionChartClient = t0 => {
  const $ = _c(80);
  const {
    vaultAddress,
    chainId
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const isMobile = useIsMobile();
  const {
    account
  } = useWeb3Context();
  const {
    positionHistory: isPositionHistoryEnabled
  } = useFeatureFlags();
  const [timeFrame, setTimeFrame] = useState(defaultTimeFrame);
  const [selectedMode, setMode] = useState("assets");
  const userAddress = account.address as Address | undefined;
  let t1;
  if ($[0] !== chainId || $[1] !== timeFrame || $[2] !== userAddress || $[3] !== vaultAddress) {
    t1 = orpc.vaultV1.userPositionHistory.queryOptions({
      input: userAddress ? {
        chainId,
        address: vaultAddress,
        userAddress,
        range: toHistoryRange(timeFrame)
      } : skipToken
    });
    $[0] = chainId;
    $[1] = timeFrame;
    $[2] = userAddress;
    $[3] = vaultAddress;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data,
    isLoading: loading,
    error
  } = useQuery(t1);
  let t2;
  if ($[5] !== error) {
    t2 = errorCodeOf(error);
    $[5] = error;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const isNotFound = t2 === "NOT_FOUND";
  let t3;
  if ($[7] !== chainId || $[8] !== userAddress || $[9] !== vaultAddress) {
    t3 = orpc.vaultV1.userPosition.queryOptions({
      input: userAddress ? {
        chainId,
        address: vaultAddress,
        userAddress
      } : skipToken
    });
    $[7] = chainId;
    $[8] = userAddress;
    $[9] = vaultAddress;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    data: userPositionData,
    error: userPositionError
  } = useQuery(t3);
  const isUserPositionNotFound = isDefined(userPositionError) && errorCodeOf(userPositionError) === "NOT_FOUND";
  const userPositionState = isUserPositionNotFound ? null : userPositionData?.position;
  const asset = data?.asset;
  const priceWad = isDefined(asset?.priceUsd) ? parseEther(asset.priceUsd.toFixed(18)) : undefined;
  const assetDecimals = asset?.decimals ?? undefined;
  let t4;
  if ($[11] !== asset) {
    t4 = !isDefined(asset) || isDefined(asset.symbol) && isDefined(asset.decimals);
    $[11] = asset;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const hasAssetMode = t4;
  const mode = hasAssetMode ? selectedMode : "assetsUsd";
  let options;
  if ($[13] !== asset || $[14] !== hasAssetMode) {
    options = [];
    if (hasAssetMode && asset?.symbol) {
      let t5;
      if ($[16] !== asset.symbol) {
        t5 = {
          value: "assets",
          label: asset.symbol
        };
        $[16] = asset.symbol;
        $[17] = t5;
      } else {
        t5 = $[17];
      }
      options.push(t5);
    }
    let t5;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        value: "assetsUsd",
        label: "USD"
      };
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    options.push(t5);
    $[13] = asset;
    $[14] = hasAssetMode;
    $[15] = options;
  } else {
    options = $[15];
  }
  const modeOptions = options;
  let formattedData;
  let t5;
  if ($[19] !== asset?.decimals || $[20] !== data?.points || $[21] !== mode) {
    let _data = [];
    bb0: switch (mode) {
      case "assets":
        {
          _data = formatTimeseriesData(data?.points.map(_temp), asset?.decimals ?? 18);
          break bb0;
        }
      case "assetsUsd":
        {
          _data = formatTimeseriesData(data?.points.map(_temp2));
        }
    }
    formattedData = removeNullYValuesFromSeries(_data);
    t5 = isSerieEmpty(formattedData);
    $[19] = asset?.decimals;
    $[20] = data?.points;
    $[21] = mode;
    $[22] = formattedData;
    $[23] = t5;
  } else {
    formattedData = $[22];
    t5 = $[23];
  }
  const hasData = !t5;
  let t6;
  if ($[24] !== data) {
    t6 = isDefined(data) && data.points.length > 0;
    $[24] = data;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const hasPoints = t6;
  let t7;
  if ($[26] !== data || $[27] !== mode) {
    t7 = mode === "assetsUsd" && isDefined(data) && isUsdHistoryUnavailable(data.points);
    $[26] = data;
    $[27] = mode;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const isUsdUnavailable = t7;
  let t8;
  if ($[29] !== error || $[30] !== isNotFound || $[31] !== isUsdUnavailable) {
    t8 = isNotFound ? undefined : error ?? (isUsdUnavailable ? new Error("USD history unavailable") : undefined);
    $[29] = error;
    $[30] = isNotFound;
    $[31] = isUsdUnavailable;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const chartError = t8;
  let t9;
  if ($[33] !== hasPoints || $[34] !== userPositionState) {
    t9 = hasPoints || isDefined(userPositionState);
    $[33] = hasPoints;
    $[34] = userPositionState;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const hasPosition = t9;
  const showEmptyState = isPositionHistoryEnabled && !loading && !chartError && (isNotFound || data?.points.length === 0) && !hasData;
  const assets_0 = useCachedValue(userPositionState === null ? "0" : userPositionState?.assets);
  const assetsUsd_0 = useCachedValue(userPositionState === null ? 0 : userPositionState?.assetsUsd);
  let t10;
  bb1: {
    switch (mode) {
      case "assets":
        {
          t10 = {
            value: assets_0,
            decimals: asset?.decimals ?? 18
          };
          break bb1;
        }
      case "assetsUsd":
        {
          t10 = {
            value: getParsedValue(assetsUsd_0, safeParseNumber),
            decimals: 18
          };
          break bb1;
        }
    }
    t10 = undefined;
  }
  const total = t10;
  const isTotalUsdMissing = mode === "assetsUsd" && userPositionState?.assetsUsd === null;
  let t11;
  bb2: {
    if (isTotalUsdMissing) {
      let t12;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = splitValueAndUnit("-");
        $[36] = t12;
      } else {
        t12 = $[36];
      }
      t11 = t12;
      break bb2;
    }
    const formattedValue = formatter.digits(mode === "assetsUsd" ? 2 : getDigitsFromPriceAndAmount(priceWad, toBigInt(total.value), assetDecimals)).trailingZero(!total.value).of(toBigInt(total.value), total.decimals);
    t11 = splitValueAndUnit(formattedValue);
  }
  const formattedTotal = t11;
  let t12;
  bb3: {
    if (isTotalUsdMissing) {
      t12 = undefined;
      break bb3;
    }
    const unit = mode === "assetsUsd" ? "$" : asset?.symbol;
    t12 = formatters.commas.unit(unit).digits(mode === "assetsUsd" ? 2 : getDigitsFromPriceAndAmount(priceWad, toBigInt(total.value), assetDecimals)).of(toBigInt(total.value), total.decimals);
  }
  const detailedFormattedTotal = t12;
  let t13;
  bb4: {
    const label = modeOptions.find(option => option.value === mode)?.label;
    if (label) {
      t13 = `${"My Deposit"} (${label})`;
      break bb4;
    }
    t13 = "My Deposit";
  }
  const title = t13;
  let t14;
  bb5: switch (mode) {
    case "assetsUsd":
      {
        t14 = "$";
        break bb5;
      }
    case "assets":
    default:
      {
        const t15 = asset?.symbol ?? "";
        let t16;
        if ($[37] !== t15) {
          t16 = formatLongString(t15);
          $[37] = t15;
          $[38] = t16;
        } else {
          t16 = $[38];
        }
        t14 = t16;
      }
  }
  const unit_0 = t14;
  if (isDefined(userPositionError) && !isUserPositionNotFound && userPositionData === undefined) {
    let t15;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <VaultYourPositionChartError />;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    return t15;
  }
  if (showEmptyState) {
    let t15;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <TableNoResults data-testid="vault-v1-your-position-empty" text="No position found." height="396px" withBorder={true} className="rounded-[20px]" />;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    return t15;
  }
  let t15;
  if ($[41] !== title) {
    t15 = <span className="text-secondary-foreground text-xs">{title}</span>;
    $[41] = title;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== detailedFormattedTotal || $[44] !== formattedTotal || $[45] !== isTotalUsdMissing || $[46] !== loading || $[47] !== mode || $[48] !== total) {
    t16 = loading && total.value === null ? <div className="gap-sm flex items-center"><Skeleton className="h-(--text-title-sm--line-height) w-[130px] rounded-[20px]" /><Skeleton className="h-[26px] w-[50px] rounded-[20px]" /></div> : <div className="gap-sm flex items-center"><Tooltip><TooltipTrigger asChild={true}><div className="flex cursor-default items-center gap-[2px]" data-testid="vault-v1-my-deposit">{mode === "assetsUsd" && !isTotalUsdMissing && <span className="text-title-sm text-muted-foreground">$</span>}<span className="text-title-sm">{formattedTotal.value}</span><span className="text-title-sm text-muted-foreground">{formattedTotal.unit}</span></div></TooltipTrigger>{!!detailedFormattedTotal && <TooltipContent side="right">{detailedFormattedTotal}</TooltipContent>}</Tooltip></div>;
    $[43] = detailedFormattedTotal;
    $[44] = formattedTotal;
    $[45] = isTotalUsdMissing;
    $[46] = loading;
    $[47] = mode;
    $[48] = total;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t15 || $[51] !== t16) {
    t17 = <div className="gap-xs flex flex-col">{t15}{t16}</div>;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== hasPosition || $[54] !== isMobile || $[55] !== loading || $[56] !== mode || $[57] !== modeOptions) {
    t18 = isMobile ? <Select options={modeOptions} value={mode} onChange={setMode} disabled={!hasPosition || loading} /> : <Tabs value={mode} onValueChange={value => setMode(value as Mode)}><SegmentedControlList size="default" aria-label="Currency">{modeOptions.map(option_0 => <SegmentedControlItem key={option_0.value} value={option_0.value} disabled={!hasPosition || loading}>{option_0.label}</SegmentedControlItem>)}</SegmentedControlList></Tabs>;
    $[53] = hasPosition;
    $[54] = isMobile;
    $[55] = loading;
    $[56] = mode;
    $[57] = modeOptions;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  const t19 = !hasPosition || !isPositionHistoryEnabled;
  let t20;
  if ($[59] !== t19 || $[60] !== timeFrame) {
    t20 = <Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} disabled={t19} />;
    $[59] = t19;
    $[60] = timeFrame;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== t18 || $[63] !== t20) {
    t21 = <div className="gap-sm flex flex-wrap items-center">{t18}{t20}</div>;
    $[62] = t18;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t17 || $[66] !== t21) {
    t22 = <div className="gap-sm flex flex-wrap items-center justify-between">{t17}{t21}</div>;
    $[65] = t17;
    $[66] = t21;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== chartError || $[69] !== formattedData || $[70] !== hasData || $[71] !== isPositionHistoryEnabled || $[72] !== loading || $[73] !== mode || $[74] !== palette || $[75] !== unit_0) {
    t23 = isPositionHistoryEnabled ? <SimpleChart statuses={{
      loading,
      error: isDefined(chartError)
    }} unit={unit_0} height="224px" series={hasData ? [{
      type: "areaspline",
      name: `Total ${mode}`,
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }] : []} additionalOptions={{
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    }} /> : <CardText text="Position history is temporarily unavailable." height="224px" />;
    $[68] = chartError;
    $[69] = formattedData;
    $[70] = hasData;
    $[71] = isPositionHistoryEnabled;
    $[72] = loading;
    $[73] = mode;
    $[74] = palette;
    $[75] = unit_0;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  let t24;
  if ($[77] !== t22 || $[78] !== t23) {
    t24 = <Card className="gap-lg py-lg px-md md:px-lg w-full flex-col" withBorder={true} data-testid="vault-v1-your-position-chart">{t22}{t23}</Card>;
    $[77] = t22;
    $[78] = t23;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  return t24;
};
export default VaultYourPositionChartClient;
function _temp(t0) {
  const {
    timestamp: timestamp_0,
    assets
  } = t0;
  return {
    x: timestamp_0,
    y: BigInt(assets)
  };
}
function _temp2(t0) {
  const {
    timestamp,
    assetsUsd
  } = t0;
  return [timestamp, assetsUsd];
}